import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { consumePendingSharedAudio } from '../lib/sharedAudio'
import { useCreateDocument } from '../hooks/useDocuments'
import { FolderSelector } from '../components/folders/FolderSelector'
import { lazy, Suspense } from 'react'
// TipTapが重い(+530KB)ため、ドキュメントタブを開いたときだけ読み込む
const NotionEditor = lazy(() => import('../components/editor/NotionEditor').then(m => ({ default: m.NotionEditor })))
import { Layout } from '../components/layout/Layout'
import { useAuth } from '../auth/AuthContext'

// カテゴリ機能は削除されました

export function CreateDocumentPage() {
  const navigate = useNavigate()
  const { firebaseUser } = useAuth()
  const createDocument = useCreateDocument()
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0])
  const [folderId, setFolderId] = useState<string | null>(null)
  const [participants] = useState<string[]>([])
  const [isCreating, setIsCreating] = useState(false)
  const [postCreateAction, setPostCreateAction] = useState<'edit' | 'transcribe' | 'format'>('edit')
  const [audioFile, setAudioFile] = useState<File | null>(null)
  // Notion風エディタ（ドキュメント作成モード）のMarkdown内容
  const [noteMarkdown, setNoteMarkdown] = useState('')
  // サイドバーの「新規議事録作成」ドロップダウンから ?method=upload|text|note で入力方法を指定できる
  const [searchParams] = useSearchParams()
  const initialMethod = searchParams.get('method')
  const [inputMethod, setInputMethod] = useState<'text' | 'upload' | 'note'>(
    initialMethod === 'upload' ? 'upload' : initialMethod === 'note' ? 'note' : 'text'
  )

  // 共有シート経由で受け取った音声ファイル（ネイティブアプリ）を自動セット
  useEffect(() => {
    if (searchParams.get('shared') !== '1') return
    const shared = consumePendingSharedAudio()
    if (shared) {
      setInputMethod('upload')
      setAudioFile(shared)
      setPostCreateAction('transcribe')
      // タイトル未入力ならファイル名（拡張子なし）を初期値に
      setTitle((prev) => prev || shared.name.replace(/\.[^.]+$/, ''))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const handleCreate = async () => {
    if (!title.trim()) {
      alert('タイトルを入力してください')
      return
    }

    setIsCreating(true)

    try {
      // ドキュメント作成モード: 作成後にMarkdownをformattedとして保存し、詳細ページへ
      if (inputMethod === 'note') {
        const documentId = await createDocument.mutateAsync({
          title: title.trim(),
          meetingDate,
          folderId,
          participants
        })

        // createDocument APIはcontentを受け付けないため、作成直後にPATCHで内容を保存する
        const markdown = noteMarkdown.trim()
        if (markdown && documentId && firebaseUser) {
          const token = await firebaseUser.getIdToken()
          const tokenResult = await firebaseUser.getIdTokenResult()
          const orgId = tokenResult.claims.orgId
          const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'
          const patchRes = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${documentId}`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              formatted: {
                markdown,
                status: 'completed',
                version: Date.now()
              }
            })
          })
          if (!patchRes.ok) {
            console.error('❌ Failed to save document content:', await patchRes.text())
          }
        }

        navigate(`/documents/${documentId}`)
        return
      }

      const documentData = {
        title: title.trim(),
        content: content.trim(),
        meetingDate,
        folderId,
        participants,
        // 初期内容がある場合は文字起こしデータとして保存
        ...(content.trim() && {
          transcript: {
            text: content.trim(),
            lang: 'ja',
            durationSec: 0,
            source: 'paste'
          }
        })
      }

      const documentId = await createDocument.mutateAsync(documentData)

      // 音声ファイルがある場合は文字起こしを実行
      if (audioFile && documentId && firebaseUser) {
        try {
          console.log('📤 Uploading audio file to Storage...', {
            fileName: audioFile.name,
            fileSize: audioFile.size,
            fileType: audioFile.type
          })

          const tokenResult = await firebaseUser.getIdTokenResult()
          const orgId = tokenResult.claims.orgId

          // Firebase Storageに直接アップロード
          const { getStorage, ref, uploadBytes } = await import('firebase/storage')
          const storage = getStorage()
          const storagePath = `audio/${orgId}/${documentId}/${Date.now()}_${audioFile.name}`
          const storageRef = ref(storage, storagePath)

          console.log('📤 Uploading to path:', storagePath)
          await uploadBytes(storageRef, audioFile)
          console.log('✅ Audio file uploaded to Storage')

          // アップロード完了後、バックエンドに文字起こしをリクエスト
          const token = await firebaseUser.getIdToken()
          const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

          console.log('🎤 Requesting transcription...')
          const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents/${documentId}/transcribe`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              audioPath: storagePath,
              fileName: audioFile.name,
              mimeType: audioFile.type
            })
          })

          if (response.ok) {
            console.log('✅ Transcription started for new document')
          } else {
            const errorText = await response.text()
            console.error('❌ Transcription failed:', errorText)
          }
        } catch (transcribeError) {
          console.error('❌ Transcription error:', transcribeError)
          // 文字起こしが失敗しても議事録作成は成功しているので続行
        }
      }

      // 作成後のアクションに応じて遷移
      switch (postCreateAction) {
        case 'edit':
          navigate(`/documents/${documentId}?tab=content`)
          break
        case 'transcribe':
          navigate(`/documents/${documentId}?tab=transcribe`)
          break
        case 'format':
          navigate(`/documents/${documentId}?tab=format`)
          break
        default:
          navigate(`/documents/${documentId}`)
      }
    } catch (error) {
      console.error('議事録作成エラー:', error)
      alert('議事録の作成に失敗しました')
    } finally {
      setIsCreating(false)
    }
  }

  const handleInputMethodChange = (method: 'text' | 'upload' | 'note') => {
    setInputMethod(method)
    // 入力方法に応じて適切な作成後アクションを設定
    switch (method) {
      case 'text':
      case 'note':
        setPostCreateAction('edit')
        break
      case 'upload':
        setPostCreateAction('transcribe')
        break
    }
  }

  const handleAudioFileSelect = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'audio/*,video/*,.mp3,.wav,.m4a,.mp4,.mov,.avi'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        setAudioFile(file)
        // 音声ファイルが選択された場合、音声ファイルアップロードタブに切り替え
        setInputMethod('upload')
        setPostCreateAction('transcribe')
      }
    }
    input.click()
  }

  const removeAudioFile = () => {
    setAudioFile(null)
    // 音声ファイルを削除した場合、テキスト入力タブに戻す
    setInputMethod('text')
    setPostCreateAction('edit')
  }

  return (
    <Layout
      title="新規議事録作成"
      showBackButton={true}
      backPath="/"
      showSidebar={true}
      selectedFolderId={folderId}
      onFolderSelect={(id) => setFolderId(id)}
    >
      <div className="container-md w-full">
          <div className="card">
            <div>
              {/* タイトル入力 */}
              <div className="form-group">
                <label className="form-label">
                  議事録タイトル <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="例: 2024年第1四半期売上会議"
                  className="form-input"
                />
              </div>

              {/* 会議日時入力 */}
              <div className="form-group">
                <label className="form-label">
                  会議日時
                </label>
                <input
                  type="date"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                  className="form-input"
                />
              </div>

              {/* カテゴリ機能は削除されました */}

              {/* フォルダ選択 */}
              <div className="form-group">
                <label className="form-label">
                  保存先フォルダ（任意）
                </label>
                <FolderSelector
                  selectedFolderId={folderId}
                  onFolderSelect={setFolderId}
                  placeholder="フォルダを選択（任意）"
                  disabled={isCreating}
                />
              </div>

              {/* 入力方法選択 */}
              <div className="form-group">
                <label className="form-label">
                  作成方法のオプション
                </label>

                {/* タブナビゲーション */}
                <div className="tab-list mb-4">
                  <button
                    type="button"
                    onClick={() => handleInputMethodChange('text')}
                    className={`tab-item${inputMethod === 'text' ? ' active' : ''}`}
                  >
                    テキスト入力
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputMethodChange('upload')}
                    className={`tab-item${inputMethod === 'upload' ? ' active' : ''}`}
                  >
                    音声ファイル
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputMethodChange('note')}
                    className={`tab-item${inputMethod === 'note' ? ' active' : ''}`}
                  >
                    ドキュメント
                  </button>
                </div>

                {/* タブコンテンツ */}
                <div style={{ border: '1px solid var(--color-border-light)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)' }}>
                  {inputMethod === 'text' && (
                    <div>
                      <h3 className="text-md font-semibold text-primary mb-1">内容を直接入力</h3>
                      <p className="form-hint mb-4">議事録の内容を直接入力・編集できます</p>
                      <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="議事録の内容を入力してください。後から編集・追加も可能です。"
                        rows={8}
                        className="form-input resize-none"
                      />
                    </div>
                  )}

                  {inputMethod === 'note' && (
                    <div>
                      <h3 className="text-md font-semibold text-primary mb-1">ドキュメントを作成</h3>
                      <p className="form-hint mb-4">Notion風エディタで直接書けます。Markdownショートカットに対応しています</p>
                      <Suspense fallback={<div className="flex items-center gap-2 py-8"><span className="spinner" /><span className="text-secondary text-sm">エディタを読み込み中...</span></div>}>
                        <NotionEditor
                        initialMarkdown={noteMarkdown}
                        onChange={setNoteMarkdown}
                      />
                      </Suspense>
                    </div>
                  )}

                  {inputMethod === 'upload' && (
                    <div>
                      <h3 className="text-md font-semibold text-primary mb-1">音声ファイルをアップロード</h3>
                      <p className="form-hint mb-4">音声ファイルをアップロードして自動で文字起こしを実行</p>

                      {audioFile ? (
                        <div className="card" style={{ padding: 'var(--space-4)', background: 'var(--color-surface)' }}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="icon-wrap icon-wrap-lg icon-wrap-accent">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                                  <path d="M9 18V5l12-2v13" />
                                  <circle cx="6" cy="18" r="3" />
                                  <circle cx="18" cy="16" r="3" />
                                </svg>
                              </span>
                              <div>
                                <p className="text-base font-medium text-primary">{audioFile.name}</p>
                                <p className="text-sm text-tertiary">
                                  {(audioFile.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={removeAudioFile}
                              className="btn-icon borderless"
                              title="ファイルを削除"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 6 6 18M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="upload-zone">
                          <div className="upload-zone-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                              <path d="M12 12v9M16 16l-4-4-4 4" />
                            </svg>
                          </div>
                          <div className="upload-zone-title">音声ファイルをアップロード</div>
                          <div className="upload-zone-hint">MP3, WAV, M4A, MP4, MOV, AVI</div>
                          <button
                            type="button"
                            onClick={handleAudioFileSelect}
                            className="btn btn-primary btn-sm"
                            style={{ marginTop: 'var(--space-4)' }}
                          >
                            ファイルを選択
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>


              {/* アクションボタン */}
              <div className="flex items-center justify-end gap-2 pt-4" style={{ borderTop: '1px solid var(--color-border-light)' }}>
                <button
                  onClick={() => navigate('/')}
                  className="btn btn-outline"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleCreate}
                  disabled={isCreating || !title.trim()}
                  className="btn btn-primary"
                  style={isCreating || !title.trim() ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                >
                  {isCreating ? (
                    <>
                      <span className="spinner sm" style={{ borderTopColor: 'currentColor' }} />
                      作成中...
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                      作成
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
      </div>
    </Layout>
  )
}
